import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  List,
  ListItem,
  Container,
  Section,
  RowColumn,
  Row,
  SectionTitle,
} from 'chaoskit/src/components'
import { tealBrightTeal } from 'chaoskit/src/assets/styles/utility/gradient'
import { isEmpty } from 'lodash'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import StyledHeading from './StyledHeading'

import Data from '~components/Data'
import { listBullet, slantBackground } from '~styles/utility'
import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import StaticFooter from '~partials/footer/StaticFooter'
import Hero, { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import Speakers from '~partials/events/Speakers'
import EventForm from '~components/EventForm'
import SlackSignUpForm from '~partials/slack/SlackSignUpForm'
import { ReactComponent as SlackSymbolSVG } from '~media/logos/slack-symbol.svg'
import GoogleStaticMap from '~components/GoogleStaticMap'

const EventSingle = ({
  data: {
    pageData: {
      frontmatter: {
        title,
        description,
        socialMediaImage,
        hideFromResources,
        date,
        dateTime,
        time,
        eventSessions,
        eventLocation,
        eventType,
        hero,
        content,
        agenda,
        speakers,
      },
      fields: { slug },
    },
  },
  pageContext: { nodeType },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout noFooter headerProps={{ sticky: false, showNav: false }}>
      <HelmetSEO
        title={title}
        description={description}
        image={socialMediaImage}
        noIndex={hideFromResources}
      />
      <NodeTypeTracker nodeType={nodeType} />
      <Hero contrast={false} css={[tealBrightTeal(theme)]}>
        <HeroContent>
          <Container>
            <Row gutter={{ base: 'base', medium: 'xlarge' }}>
              <RowColumn
                size={{ medium: 6 }}
                css={{
                  textAlign: 'center',

                  [theme.mq.medium]: {
                    textAlign: 'left',
                  },
                }}
                className="u-contrast"
              >
                <div
                  css={{
                    [theme.mq.medium]: {
                      position: 'sticky',
                      top: theme.space.base,
                    },
                  }}
                >
                  <StyledHeading
                    as="h5"
                    css={[
                      theme.fontSize.xlarge__fluid,
                      {
                        color: `${theme.contrast.muted} !important`,
                      },
                    ]}
                  >
                    {eventType === 'bootcamp' ? eventType : 'Virtual talk'}
                  </StyledHeading>
                  <HeroTitle as="h2" css={{ marginTop: 0 }}>
                    <Data>{hero.heading}</Data>
                  </HeroTitle>
                  <HeroTitleSub>
                    <MDXRenderer>{hero.content}</MDXRenderer>
                  </HeroTitleSub>
                  {(!isEmpty(eventLocation) || (date && time)) && (
                    <div
                      css={{
                        display: 'grid',
                        gap: theme.space.base,
                        marginTop: theme.space.large,

                        [theme.mq.small]: {
                          gridAutoColumns: '1fr',
                          gridAutoFlow: 'column',
                        },
                      }}
                    >
                      {!isEmpty(eventLocation) && (
                        <div>
                          <StyledHeading
                            as="h5"
                            css={{
                              marginBottom: theme.space.small,
                              fontSize: theme.fontSize.medium,
                            }}
                          >
                            Location
                          </StyledHeading>
                          <address
                            css={{
                              fontWeight: theme.fontWeight.bold,
                              fontSize: theme.fontSize.large,
                            }}
                          >
                            <div>{eventLocation.address1}</div>
                            {eventLocation.address2 && (
                              <div>{eventLocation.address2}</div>
                            )}
                            <div>
                              {eventLocation.city}, {eventLocation.state}{' '}
                              {eventLocation.zip}
                            </div>
                          </address>
                        </div>
                      )}

                      {date && time && (
                        <div>
                          <StyledHeading
                            as="h5"
                            css={{
                              marginBottom: theme.space.small,
                              fontSize: theme.fontSize.medium,
                            }}
                          >
                            Time
                          </StyledHeading>
                          <time
                            itemProp="datePublished"
                            dateTime={dateTime}
                            css={{
                              fontWeight: theme.fontWeight.bold,
                              fontSize: theme.fontSize.large,
                            }}
                          >
                            {date}
                            <br />
                            <Data>{time}</Data>
                          </time>
                        </div>
                      )}
                    </div>
                  )}

                  {hero.meta && (
                    <div
                      css={{
                        fontSize: theme.fontSize.small,
                        marginTop: theme.space.large,
                      }}
                    >
                      <Data>{hero.meta}</Data>
                    </div>
                  )}

                  {!isEmpty(eventLocation) && (
                    <GoogleStaticMap
                      location={eventLocation}
                      css={{
                        boxShadow: theme.boxShadow.base,
                        borderRadius: theme.borderRadius.large,
                        marginTop: theme.space.large,
                      }}
                    />
                  )}
                </div>
              </RowColumn>
              <RowColumn size={{ medium: 6 }}>
                <EventForm
                  formId={slug}
                  sessions={eventSessions}
                  title={hero.form.heading}
                  fieldValues={{
                    programID: hero.form.programId,
                    bigMarkerSession: hero.form?.bigMarkerSession,
                  }}
                  variation={eventType === 'bootcamp' ? 'bootcamp' : 'webinar'}
                />
              </RowColumn>
            </Row>
          </Container>
        </HeroContent>
      </Hero>
      <Section space="xlarge">
        <Container>
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,

              [theme.mq.medium]: {
                gridTemplateColumns: '1fr 2px 1fr',
              },
            }}
          >
            <div>
              <StyledHeading
                as="h5"
                css={{
                  fontSize: theme.fontSize.medium,
                  marginBottom: theme.space.large,
                }}
              >
                About This {eventType}
              </StyledHeading>
              <div css={[theme.fontSize.large__fluid]}>
                <MDXRenderer>{content}</MDXRenderer>
              </div>
              <StyledHeading
                as="h5"
                css={{
                  fontSize: theme.fontSize.medium,
                  marginTop: theme.space.xlarge,
                  marginBottom: theme.space.large,
                }}
              >
                Agenda
              </StyledHeading>
              <List space="base" css={listBullet(theme)}>
                {agenda.map((item) => (
                  <ListItem key={item.content}>
                    <MDXRenderer>{item.content}</MDXRenderer>
                  </ListItem>
                ))}
              </List>
            </div>
            <div
              css={{
                background: theme.color.border.base,
                height: 2,
                display: 'block',

                [theme.mq.medium]: {
                  height: 'auto',
                },
              }}
            />
            <div>
              <StyledHeading
                as="h5"
                css={{
                  fontSize: theme.fontSize.medium,
                  marginBottom: theme.space.large,
                }}
              >
                {eventType === 'bootcamp'
                  ? 'Your bootcamp leads'
                  : 'About the speakers'}
              </StyledHeading>
              <Speakers speakers={speakers} />
            </div>
          </div>
        </Container>
      </Section>
      <Section className="u-contrast" css={[slantBackground(theme)]}>
        <Container>
          <SlackSymbolSVG
            style={{ height: 60 }}
            css={{
              display: 'block',
              marginBottom: theme.space.base,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          />
          <SectionTitle
            title="Join the Chaos Engineering Slack"
            sub="Connect with 5,000+ engineers who are building more reliable systems with Chaos Engineering."
          />
          <div
            css={{
              display: 'grid',

              [theme.mq.medium]: {
                gridTemplateColumns: '0.5fr',
                justifyContent: 'center',
              },
            }}
          >
            <SlackSignUpForm inline />
          </div>
        </Container>
      </Section>

      <StaticFooter />
    </BaseLayout>
  )
}

EventSingle.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default EventSingle

export const pageQuery = graphql`
  query EventSingle($slug: String!) {
    pageData: mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/events/" }
    ) {
      ...EventFragment
    }
  }
`
